export type ProviderLinkItem = {
  url: string;
  domain: string;
  label: string | null;
  title: string | null;
};

const normalizeDomain = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '')
    .split('/')[0];

const toStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

export const parseProviderLinks = (value: unknown): ProviderLinkItem[] => {
  if (!Array.isArray(value)) return [];

  const links = new Map<string, ProviderLinkItem>();

  value.forEach((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return;

    const record = item as Record<string, unknown>;
    const url = toStringOrNull(record.url);
    if (!url || (!url.startsWith('http://') && !url.startsWith('https://'))) return;

    const label = toStringOrNull(record.text) ?? toStringOrNull(record.label);
    const title = toStringOrNull(record.title);
    const dedupeKey = `${url}::${label ?? ''}::${title ?? ''}`;

    if (!links.has(dedupeKey)) {
      links.set(dedupeKey, {
        url,
        domain: normalizeDomain(url),
        label,
        title,
      });
    }
  });

  return Array.from(links.values());
};

export const mergeResponseLinks = (
  citations: Array<{ url: string; domain: string }>,
  providerLinks: ProviderLinkItem[],
): ProviderLinkItem[] => {
  const merged = new Map<string, ProviderLinkItem>();

  providerLinks.forEach((link) => {
    const dedupeKey = `${link.url}::${link.label ?? ''}`;
    merged.set(dedupeKey, link);
  });

  citations.forEach((citation) => {
    if (!citation.url) return;
    const dedupeKey = `${citation.url}::`;
    if (!merged.has(dedupeKey)) {
      merged.set(dedupeKey, {
        url: citation.url,
        domain: citation.domain,
        label: null,
        title: null,
      });
    }
  });

  return Array.from(merged.values());
};
